import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import useContractDraftingService from '../../app/services/hooks/useContractDraftingService';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import ButtonTableAction from '../../components/buttons/ButtonTableAction';
import DefaultDataTable from '../../components/default/DefaultDataTable';
import DefaultCard from '../../components/default/DefaultCard';
import PageHeader from '../../components/PageHeader/PageHeader';
import InputField from '../../components/InputField';
import { AppContext } from '../../contexts/AppContext';
import { ServiceResponse } from '../../app/services/shared/interfaces';

interface ContractType {
    id: number;
    name: string;
    price: number;
    has_articles: boolean;
}

interface ContractRequest {
    id: number;
    reference_code: string;
    contract_type: ContractType;
    status: string;
    payment_status: string;
    payment_amount: number;
    created_at: string;
}

interface SignedContract {
    invitation_id: number;
    signature_status: string;
    has_signed: boolean;
    can_sign: boolean;
    sign_token: string | null;
    signed_at: string | null;
    contract_request_id: number;
    reference_code: string;
    contract_type_name: string;
    contract_signature_status: string;
    fully_signed: boolean;
    signatures_completed: number;
    signatures_required: number;
    requested_by: string;
    document_url: string | null;
    created_at: string;
}

type TabKey = 'mine' | 'signing';

const MyContracts: React.FC = () => {
    const history = useHistory();
    const { showLoading, hideLoading } = useContext(AppContext);
    const {
        getMyContractRequests,
        getMySignedContracts,
        downloadFinalDocument,
        fetchingMyRequests,
        fetchingMySignedContracts
    } = useContractDraftingService();

    const [activeTab, setActiveTab] = useState<TabKey>('mine');

    const [requests, setRequests] = useState<ContractRequest[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [filter, setFilter] = useState({
        page: 1,
        per_page: 10,
        search: '',
        status: ''
    });

    const [signedContracts, setSignedContracts] = useState<SignedContract[]>([]);
    const [signedTotalRows, setSignedTotalRows] = useState(0);
    const [signedFilter, setSignedFilter] = useState({
        page: 1,
        per_page: 10,
        search: ''
    });

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.search, filter.status]);

    // La pestaña de firmas se consulta solo cuando está a la vista para no pedir
    // datos que nadie va a mirar.
    useEffect(() => {
        if (activeTab === 'signing') {
            reloadSignedTable();
        }
    }, [activeTab, signedFilter.page, signedFilter.per_page, signedFilter.search]);

    useEffect(() => {
        if (fetchingMyRequests || fetchingMySignedContracts) {
            showLoading?.();
        } else {
            hideLoading?.();
        }
    }, [fetchingMyRequests, fetchingMySignedContracts]);

    const reloadTable = () => {
        getMyContractRequests({
            page: filter.page,
            page_size: filter.per_page,
            search: filter.search,
            status: filter.status || undefined
        }, {
            onSuccess: (res: ServiceResponse) => {
                setRequests(res.data.requests || []);
                setTotalRows(res.data.total_rows || res.data.total || 0);
            },
            onError: (res: ServiceResponse) => {
                toast.error(res.message || 'Error al cargar las solicitudes');
            }
        });
    };

    const reloadSignedTable = () => {
        getMySignedContracts({
            page: signedFilter.page,
            page_size: signedFilter.per_page,
            search: signedFilter.search
        }, {
            onSuccess: (res: ServiceResponse) => {
                setSignedContracts(res.data.contracts || []);
                setSignedTotalRows(res.data.total_rows || res.data.total || 0);
            },
            onError: (res: ServiceResponse) => {
                toast.error(res.message || 'Error al cargar los contratos que firmas');
            }
        });
    };

    const getStatusBadge = (status: string) => {
        const statusMap: Record<string, { label: string; class: string }> = {
            'draft': { label: 'Borrador', class: 'bg-secondary' },
            'preview_generated': { label: 'Vista Previa', class: 'bg-info' },
            'pending_payment': { label: 'Pendiente Pago', class: 'bg-warning' },
            'paid': { label: 'Pagado', class: 'bg-success' },
            'completed': { label: 'Completado', class: 'bg-success' },
            'cancelled': { label: 'Cancelado', class: 'bg-danger' }
        };
        const statusInfo = statusMap[status] || { label: status, class: 'bg-secondary' };
        return <span className={`badge ${statusInfo.class}`}>{statusInfo.label}</span>;
    };

    const getPaymentStatusBadge = (paymentStatus: string) => {
        const statusMap: Record<string, { label: string; class: string }> = {
            'pending': { label: 'Pendiente', class: 'bg-warning' },
            'paid': { label: 'Pagado', class: 'bg-success' },
            'failed': { label: 'Fallido', class: 'bg-danger' },
            'cancelled': { label: 'Cancelado', class: 'bg-secondary' }
        };
        const statusInfo = statusMap[paymentStatus] || { label: paymentStatus, class: 'bg-secondary' };
        return <span className={`badge ${statusInfo.class}`}>{statusInfo.label}</span>;
    };

    const handleViewRequest = (requestId: number, status: string) => {
        if (status === 'draft') {
            history.push(`/intranet/contracts/${requestId}/form`);
        } else if (status === 'preview_generated' || status === 'pending_payment') {
            history.push(`/intranet/contracts/${requestId}/preview`);
        } else if (status === 'paid' || status === 'completed') {
            history.push(`/intranet/contracts/${requestId}/payment-success`);
        }
    };

    const handleDownloadFinal = (requestId: number) => {
        downloadFinalDocument(requestId);
    };

    const getSignatureBadge = (row: SignedContract) => {
        const statusMap: Record<string, { label: string; class: string }> = {
            'pending': { label: 'Pendiente', class: 'bg-warning' },
            'sent': { label: 'Pendiente', class: 'bg-warning' },
            'viewed': { label: 'Pendiente', class: 'bg-warning' },
            'signed': { label: 'Firmado', class: 'bg-success' },
            'rejected': { label: 'Rechazado', class: 'bg-danger' },
            'expired': { label: 'Expirado', class: 'bg-secondary' }
        };
        const statusInfo = statusMap[row.signature_status] || { label: row.signature_status, class: 'bg-secondary' };
        return <span className={`badge ${statusInfo.class}`}>{statusInfo.label}</span>;
    };

    const handleSignNow = (row: SignedContract) => {
        if (!row.sign_token) {
            toast.info('Esta firma ya no está disponible.');
            return;
        }
        history.push(`/sign/${row.sign_token}`);
    };

    const handleDownloadSigned = (row: SignedContract) => {
        if (!row.document_url) {
            toast.info('El documento estará disponible una vez que completes tu firma.');
            return;
        }
        window.open(row.document_url, '_blank');
    };

    const columns = [
        {
            name: 'Código',
            selector: (row: ContractRequest) => row.reference_code,
            sortable: true,
            cell: (row: ContractRequest) => (
                <span className="font-monospace fw-bold">{row.reference_code}</span>
            )
        },
        {
            name: 'Tipo de Contrato',
            selector: (row: ContractRequest) => row.contract_type.name,
            sortable: true
        },
        {
            name: 'Estado',
            selector: (row: ContractRequest) => row.status,
            sortable: true,
            cell: (row: ContractRequest) => getStatusBadge(row.status)
        },
        {
            name: 'Pago',
            selector: (row: ContractRequest) => row.payment_status,
            sortable: true,
            cell: (row: ContractRequest) => getPaymentStatusBadge(row.payment_status)
        },
        {
            name: 'Monto',
            selector: (row: ContractRequest) => row.payment_amount,
            sortable: true,
            cell: (row: ContractRequest) => (
                <span className="fw-bold">${row.payment_amount.toLocaleString('es-CL')}</span>
            )
        },
        {
            name: 'Fecha',
            selector: (row: ContractRequest) => new Date(row.created_at).toLocaleDateString('es-CL'),
            sortable: true
        },
        {
            name: 'Acciones',
            selector: (row: ContractRequest) => row.id,
            sortable: false,
            cell: (row: ContractRequest) => (
                <div className="d-flex align-items-center">
                    <ButtonTableAction
                        callbackFunction={() => handleViewRequest(row.id, row.status)}
                        title="Ver"
                        classIcon="fa-eye"
                        colorIcon="text-info"
                        sizeIcon="16"
                    />
                    {(row.status === 'paid' || row.status === 'completed') && (
                        <ButtonTableAction
                            callbackFunction={() => handleDownloadFinal(row.id)}
                            title="Descargar"
                            classIcon="fa-download"
                            colorIcon="text-success"
                            sizeIcon="16"
                        />
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '100px'
        }
    ];

    const signedColumns = [
        {
            name: 'Código',
            selector: (row: SignedContract) => row.reference_code,
            sortable: true,
            cell: (row: SignedContract) => (
                <span className="font-monospace fw-bold">{row.reference_code}</span>
            )
        },
        {
            name: 'Tipo de Contrato',
            selector: (row: SignedContract) => row.contract_type_name,
            sortable: true
        },
        {
            name: 'Solicitado por',
            selector: (row: SignedContract) => row.requested_by,
            sortable: true
        },
        {
            name: 'Mi firma',
            selector: (row: SignedContract) => row.signature_status,
            sortable: true,
            cell: (row: SignedContract) => getSignatureBadge(row)
        },
        {
            name: 'Firmas',
            selector: (row: SignedContract) => row.signatures_completed,
            sortable: true,
            cell: (row: SignedContract) => (
                <span className={row.fully_signed ? 'fw-bold text-success' : ''}>
                    {row.signatures_completed} de {row.signatures_required}
                </span>
            )
        },
        {
            name: 'Fecha',
            selector: (row: SignedContract) => new Date(row.created_at).toLocaleDateString('es-CL'),
            sortable: true
        },
        {
            name: 'Acciones',
            selector: (row: SignedContract) => row.invitation_id,
            sortable: false,
            cell: (row: SignedContract) => (
                <div className="d-flex align-items-center">
                    {row.can_sign && (
                        <ButtonTableAction
                            callbackFunction={() => handleSignNow(row)}
                            title="Firmar"
                            classIcon="fa-file-signature"
                            colorIcon="text-primary"
                            sizeIcon="16"
                        />
                    )}
                    {row.has_signed && row.document_url && (
                        <ButtonTableAction
                            callbackFunction={() => handleDownloadSigned(row)}
                            title={row.fully_signed ? 'Descargar documento firmado' : 'Descargar (faltan firmas)'}
                            classIcon="fa-download"
                            colorIcon="text-success"
                            sizeIcon="16"
                        />
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '100px'
        }
    ];

    return (
        <div className="container-fluid">
            <PageHeader
                title=""
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Mis Contratos', isCurrent: true }
                ]}
            />

            <DefaultCard>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h1 className="page-title">Mis Contratos</h1>
                        <p className="page-description">
                            Contratos que solicitaste y aquellos en los que participas como firmante
                        </p>
                    </div>
                    <ButtonGeneral
                        type="button"
                        variant="primary"
                        title="Nuevo Contrato"
                        onClick={() => history.push('/intranet/contracts/new')}
                        icon="fa-plus"
                        size="small"
                    />
                </div>

                <ul className="nav nav-tabs mb-4">
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${activeTab === 'mine' ? 'active' : ''}`}
                            onClick={() => setActiveTab('mine')}
                        >
                            <i className="fas fa-file-contract me-2"></i>
                            Mis contratos
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${activeTab === 'signing' ? 'active' : ''}`}
                            onClick={() => setActiveTab('signing')}
                        >
                            <i className="fas fa-file-signature me-2"></i>
                            Donde firmo
                        </button>
                    </li>
                </ul>

                {activeTab === 'mine' ? (
                    <>
                        <div className="row mb-3">
                            <div className="col-md-4">
                                <InputField
                                    label="Buscar"
                                    value={filter.search}
                                    onChange={(value) => setFilter({ ...filter, search: value, page: 1 })}
                                    placeholder="Buscar por código o tipo..."
                                />
                            </div>
                        </div>

                        <DefaultDataTable
                            columns={columns}
                            data={requests}
                            paginationTotalRows={totalRows}
                            filter={filter}
                            setFilter={setFilter}
                            pagination={true}
                            progressPending={fetchingMyRequests}
                        />
                    </>
                ) : (
                    <>
                        <p className="text-muted small">
                            Contratos de otras personas en los que fuiste invitado a firmar. Una vez
                            firmados por todas las partes puedes descargar la versión con todas las firmas.
                        </p>

                        <div className="row mb-3">
                            <div className="col-md-4">
                                <InputField
                                    label="Buscar"
                                    value={signedFilter.search}
                                    onChange={(value) => setSignedFilter({ ...signedFilter, search: value, page: 1 })}
                                    placeholder="Buscar por código o tipo..."
                                />
                            </div>
                        </div>

                        <DefaultDataTable
                            columns={signedColumns}
                            data={signedContracts}
                            paginationTotalRows={signedTotalRows}
                            filter={signedFilter}
                            setFilter={setSignedFilter}
                            pagination={true}
                            progressPending={fetchingMySignedContracts}
                        />
                    </>
                )}
            </DefaultCard>
        </div>
    );
};

export default MyContracts;
